import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Star, Quote, Send, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER_REVIEWS = [
  { name: 'Maria T.', rating: 5, text: { it: 'Hanno trovato documenti della mia famiglia risalenti al 1850! Un servizio incredibile e professionale.', en: 'They found documents of my family dating back to 1850! Incredible and professional service.', es: 'Encontraron documentos de mi familia que datan de 1850! Un servicio increible y profesional.', pt: 'Encontraram documentos da minha familia datando de 1850! Servico incrivel e profissional.' }, source: 'google' },
  { name: 'Roberto C.', rating: 5, text: { it: 'Grazie a Italian Roots Discovery ho scoperto le origini della mia famiglia in Calabria. Esperienza emozionante.', en: 'Thanks to Italian Roots Discovery I discovered my family origins in Calabria. Emotional experience.', es: 'Gracias a Italian Roots Discovery descubri los origenes de mi familia en Calabria. Experiencia emocional.', pt: 'Gracas ao Italian Roots Discovery descobri as origens da minha familia na Calabria. Experiencia emocionante.' }, source: 'google' },
  { name: 'Ana L.', rating: 5, text: { it: 'Laura e Giosia sono stati fantastici. Hanno gestito tutto con cura e dedizione. Consigliatissimo!', en: 'Laura and Giosia were fantastic. They handled everything with care and dedication. Highly recommended!', es: 'Laura y Giosia fueron fantasticos. Manejaron todo con cuidado y dedicacion. Muy recomendado!', pt: 'Laura e Giosia foram fantasticos. Cuidaram de tudo com atencao e dedicacao. Super recomendado!' }, source: 'google' },
  { name: 'James W.', rating: 4, text: { it: 'Ottimo servizio di ricerca genealogica. Hanno trovato informazioni che non avrei mai trovato da solo.', en: 'Great genealogical research service. They found information I would never have found on my own.', es: 'Gran servicio de investigacion genealogica. Encontraron informacion que nunca habria encontrado solo.', pt: 'Otimo servico de pesquisa genealogica. Encontraram informacoes que eu nunca teria encontrado sozinho.' }, source: 'google' },
];

const ReviewsWidget = () => {
  const { language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', rating: 5, text: '' });

  const t = {
    it: { title: 'Cosa Dicono i Nostri Clienti', subtitle: 'Recensioni verificate da clienti reali', write: 'Scrivi una Recensione', placeholder: 'Racconta la tua esperienza...', send: 'Invia Recensione', thanks: 'Grazie! La tua recensione sarà pubblicata dopo approvazione.', name: 'Il tuo nome', email: 'La tua email' },
    en: { title: 'What Our Clients Say', subtitle: 'Verified reviews from real clients', write: 'Write a Review', placeholder: 'Tell us about your experience...', send: 'Submit Review', thanks: 'Thank you! Your review will be published after approval.', name: 'Your name', email: 'Your email' },
    es: { title: 'Lo Que Dicen Nuestros Clientes', subtitle: 'Reseñas verificadas de clientes reales', write: 'Escribe una Reseña', placeholder: 'Cuenta tu experiencia...', send: 'Enviar Reseña', thanks: 'Gracias! Tu reseña sera publicada tras aprobacion.', name: 'Tu nombre', email: 'Tu email' },
    pt: { title: 'O Que Dizem Nossos Clientes', subtitle: 'Avaliacoes verificadas de clientes reais', write: 'Escreva uma Avaliacao', placeholder: 'Conte sua experiencia...', send: 'Enviar Avaliacao', thanks: 'Obrigado! Sua avaliacao sera publicada apos aprovacao.', name: 'Seu nome', email: 'Seu email' },
  };
  const txt = t[language] || t.en;

  useEffect(() => {
    fetch(`${API_URL}/api/reviews`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.reviews?.length > 0) {
          setReviews(d.reviews);
        } else {
          setReviews(PLACEHOLDER_REVIEWS.map((r, i) => ({ ...r, id: `placeholder-${i}`, text: r.text[language] || r.text.en })));
        }
      })
      .catch(() => {
        setReviews(PLACEHOLDER_REVIEWS.map((r, i) => ({ ...r, id: `placeholder-${i}`, text: r.text[language] || r.text.en })));
      });
  }, [language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/reviews?name=${encodeURIComponent(form.name)}&email=${encodeURIComponent(form.email)}&rating=${form.rating}&text=${encodeURIComponent(form.text)}&language=${language}`, { method: 'POST' });
      if (res.ok) { setSubmitted(true); setShowForm(false); }
    } catch { /* silent — review submission network failure */ }
  };

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white" data-testid="reviews-widget">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-1 mb-3">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {txt.title}
          </h2>
          <p className="text-gray-500">{txt.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {reviews.slice(0, 4).map((review, i) => (
            <div key={review.id || i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-testid={`review-card-${i}`}>
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} className={`w-3.5 h-3.5 ${s <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-200'}`} />
                ))}
                {review.source === 'google' && (
                  <span className="ml-auto text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">Google</span>
                )}
              </div>
              <Quote className="w-4 h-4 text-blue-200 mb-2" />
              <p className="text-sm text-gray-600 leading-relaxed mb-3 line-clamp-4">{typeof review.text === 'string' ? review.text : (review.text[language] || review.text.en)}</p>
              <p className="text-sm font-semibold text-gray-800">{review.name}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          {submitted ? (
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-5 py-3 rounded-full">
              <Check className="w-4 h-4" /> {txt.thanks}
            </div>
          ) : showForm ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto bg-white rounded-xl p-6 shadow-sm border border-gray-100 space-y-3 text-left">
              <input type="text" required placeholder={txt.name} value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none" />
              <input type="email" required placeholder={txt.email} value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none" />
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button" onClick={() => setForm({...form, rating: s})}>
                    <Star className={`w-6 h-6 transition-colors ${s <= form.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
              <textarea required placeholder={txt.placeholder} value={form.text} onChange={e => setForm({...form, text: e.target.value})} rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-400 outline-none" />
              <button type="submit" className="w-full py-2 bg-blue-600 text-white font-medium rounded-lg text-sm hover:bg-blue-700 flex items-center justify-center gap-2">
                <Send className="w-3.5 h-3.5" /> {txt.send}
              </button>
            </form>
          ) : (
            <button onClick={() => setShowForm(true)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-full text-sm font-medium hover:border-blue-400 hover:text-blue-600 transition-colors" data-testid="write-review-btn">
              {txt.write}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewsWidget;
